import { h, Component } from 'preact'
import { TFunctionDetailedResult } from 'i18next'
import classNames from 'classnames'
import { initI18n, i18n } from 'i18n/i18n'

import { TransferFeesObj, CurrencyExchangeDetails, ChosenItemTransfer } from 'models/exchange-transfer-calculate'
import { CalculatorDataObj, ChosenCountry } from 'models/calculator-foreign'
import { ExchangeDirection } from 'models/calculator'

import { debounce } from 'utils/functions'

import { exchangeTransfer } from 'services/exchange-transfer-calculate'
import { checkNetworkQuality } from 'services/slow-network'
import { Domains } from 'services/domains-api'

import { SelectOption } from 'common-modules/select-preact/select-preact'
import { MainCalculatorContinueButton } from 'common-modules/calculator-continue-button'
import { CalculatorMainError, NetworkStatusType } from 'common-modules/calculator-main-error'

import {
  defaultutTranserTypeData,
  defaultutCurrencyExchangeDetails,
  defaultutExchangeCalculation,
  defaultutChosenCountry,
  calculatorTimingConfig,
} from '../config'
import CalculatorData from './caculator-data'
import CalculatorInput from './calculator-input'
import CalculatorSelectCountry from './calculator-country-select'
import CalculatorSelectTransfer from './calculator-select-transfer'

interface CalculatorFormViewProps {
  currencies: string[]
  payoutOwner: string[]
  domains: Domains
}

interface CalculatorFormViewState {
  currencyExchangeDetails: CurrencyExchangeDetails
  exchangeCalculation: CalculatorDataObj
  chosenItemTransfer: ChosenItemTransfer
  chosenCountry?: ChosenCountry
  transferFees?: TransferFeesObj[]
  errorStatusMessage?: string | null
  errorNetworkStatus?: NetworkStatusType
  countdown: number
}

export default class MainCalculatorFormView extends Component<CalculatorFormViewProps, CalculatorFormViewState> {
  private abortController: AbortController | undefined
  private refreshRateTimer: ReturnType<typeof setTimeout> | undefined
  private countdownTimer: ReturnType<typeof setInterval> | undefined

  public constructor(props) {
    super(props)

    this.state = {
      currencyExchangeDetails: defaultutCurrencyExchangeDetails,
      exchangeCalculation: defaultutExchangeCalculation,
      chosenCountry: defaultutChosenCountry,
      chosenItemTransfer: {
        chosenItemTransferType: defaultutTranserTypeData[0].transfer_type,
        chosenItemTransferFee: defaultutTranserTypeData[0].fee,
      },
      transferFees: defaultutTranserTypeData,
      countdown: calculatorTimingConfig.pollingIntervalSeconds,
    }
  }

  public componentDidMount = async (): Promise<void> => {
    await initI18n()
  }

  public componentDidUpdate = async (prevProps, prevState: Readonly<CalculatorFormViewState>): Promise<void> => {
    if (prevProps.domains !== this.props.domains && this.props.domains?.base_user) {
      this.calculateExchangeTransfer()
    }

    if (this.isCalculatorFormViewUpdate(prevState)) {
      const networkType = await checkNetworkQuality()
      const debounceTime = this.calculateDebounceTime(networkType)
      this.setErrorNetworkStatus(networkType)
      debounce(() => this.calculateExchangeTransfer(), debounceTime)
    }
  }

  public componentWillUnmount = (): void => {
    clearTimeout(this.refreshRateTimer)
    clearInterval(this.countdownTimer)
    this.abortController?.abort()
  }

  public render() {
    return (
      <div id="preact_root">
        <div className="hero-form">
          <div className="hero-form__wrap">
            <CalculatorInput
              label={i18n('calculatorForegin.amountYouSend')}
              amount={this.state.currencyExchangeDetails.baseAmount}
              handleAmountChange={this.handleBaseAmountChange}
              isSpinner={false}
              wrapperBoxClassNames={classNames({
                'hero-form__error':
                  this.state.errorStatusMessage === 'CURRENCY_NOT_SUPPORTED' &&
                  this.hasExchangeAmountError('baseAmount'),
              })}
              classNames={classNames('bem-text-input', 'in-hero-form-big-enter-amount', 'w-input', {
                'hero-form__error': this.shouldShowAmountError(
                  this.state.currencyExchangeDetails.baseAmount,
                  'baseAmount',
                ),
              })}
              wrapperClassNames="hero-form__text-input-holder"
              handleCurrencyChange={this.handleBaseCurrencyChange}
              currencies={this.props.currencies}
              value={this.state.currencyExchangeDetails.baseCurrency}
              errorMessageCurrency={
                this.state.errorStatusMessage === 'CURRENCY_NOT_SUPPORTED'
                  ? this.getExchangeAmountErrorMessage('baseAmount')
                  : this.shouldShowAmountError(this.state.currencyExchangeDetails.baseAmount, 'baseAmount')
                  ? i18n('calculatorForegin.minAmount')
                  : null
              }
            />
            <CalculatorSelectCountry
              onChange={this.handleCountryChange}
              chosenItem={this.state.chosenCountry}
              payoutOwner={this.props.payoutOwner}
              errorMessageCurrency={
                this.state.errorStatusMessage === 'CURRENCY_COUNTRY_PAIR_NOT_SUPPORTED'
                  ? this.getExchangeAmountErrorMessage()
                  : null
              }
              wrapperBoxClassNames={classNames({
                'hero-form__error': this.state.errorStatusMessage === 'CURRENCY_COUNTRY_PAIR_NOT_SUPPORTED',
              })}
            />
            <CalculatorSelectTransfer
              onChange={this.handleTransferFeesChange}
              chosenItem={this.state.chosenItemTransfer}
              transferFees={this.state.transferFees}
            />
            <CalculatorInput
              label={i18n('calculatorForegin.amountRecipientReceive')}
              amount={this.state.currencyExchangeDetails.counterAmount}
              handleAmountChange={this.handleCounterAmountChange}
              isSpinner={false}
              wrapperBoxClassNames={classNames({
                'hero-form__error':
                  this.state.errorStatusMessage === 'CURRENCY_NOT_SUPPORTED' &&
                  this.hasExchangeAmountError('counterAmount'),
              })}
              classNames={classNames('bem-text-input', 'in-hero-form-big-enter-amount', 'w-input', {
                'hero-form__error': this.shouldShowAmountError(
                  this.state.currencyExchangeDetails.counterAmount,
                  'counterAmount',
                ),
              })}
              wrapperClassNames="hero-form__text-input-holder"
              handleCurrencyChange={this.handleCounterCurrencyChange}
              currencies={this.props.currencies}
              value={this.state.currencyExchangeDetails.counterCurrency}
              errorMessageCurrency={
                this.state.errorStatusMessage === 'CURRENCY_NOT_SUPPORTED'
                  ? this.getExchangeAmountErrorMessage('counterAmount')
                  : this.shouldShowAmountError(this.state.currencyExchangeDetails.counterAmount, 'counterAmount')
                  ? i18n('calculatorForegin.minAmount')
                  : null
              }
            />
            <CalculatorData
              sold_amount={this.state.exchangeCalculation.sold_amount}
              sold_currency={this.state.exchangeCalculation.sold_currency}
              average_rate={this.state.exchangeCalculation.average_rate}
              exchange_amount={this.state.exchangeCalculation.exchange_amount}
              transfer_type_fee={this.state.exchangeCalculation.transfer_type_fee}
              fee_amount={this.state.exchangeCalculation.fee_amount}
              fee_percent={this.state.exchangeCalculation.fee_percent}
              fee={this.getOriginalTransferFee(this.state.chosenItemTransfer.chosenItemTransferType)}
              bought_amount={this.state.exchangeCalculation.bought_amount}
              bought_currency={this.state.exchangeCalculation.bought_currency}
              countdown={this.state.countdown}
            />
            <MainCalculatorContinueButton
              hrefLink={this.ftLinkParams}
              login={this.props.domains.login}
              registry={this.props.domains.registry}
            />
          </div>
          {this.shouldShowError && <CalculatorMainError status={this.state.errorNetworkStatus} />}
        </div>
      </div>
    )
  }

  // Helpers
  public shouldShowAmountError(
    amount: CurrencyExchangeDetails['baseAmount'] | CurrencyExchangeDetails['counterAmount'] | '',
    field: 'baseAmount' | 'counterAmount' = 'baseAmount',
  ): boolean {
    const isCurrencyNotSupported =
      this.hasExchangeAmountError(field) && this.state.errorStatusMessage === 'CURRENCY_NOT_SUPPORTED'

    return isCurrencyNotSupported || Number(amount) <= 0
  }

  public calculateDebounceTime = (networkType: NetworkStatusType): number => {
    const { debounceMilliseconds } = calculatorTimingConfig
    return networkType === 'slow'
      ? debounceMilliseconds.slow
      : networkType === 'unknown'
      ? debounceMilliseconds.unknown
      : debounceMilliseconds.fast
  }

  public startTimer = (): void => {
    const pollingTimeSeconds = calculatorTimingConfig.pollingIntervalSeconds

    this.setState({ countdown: pollingTimeSeconds })

    this.refreshRateTimer = setTimeout(() => {
      this.calculateExchangeTransfer()
    }, pollingTimeSeconds * 1000)

    this.countdownTimer = setInterval(() => {
      this.setState((prevState) => ({
        countdown: prevState.countdown > 0 ? prevState.countdown - 1 : 0,
      }))
    }, 1000)
  }

  public calculateExchangeTransfer = async (): Promise<void> => {
    clearTimeout(this.refreshRateTimer)
    clearInterval(this.countdownTimer)
    this.abortController?.abort()
    this.abortController = new AbortController()

    const stateNameUpdate = this.state.currencyExchangeDetails.direction === 'back' ? 'baseAmount' : 'counterAmount'
    const stateUpdateItem = this.state.currencyExchangeDetails.direction === 'back' ? 'bought_amount' : 'sold_amount'

    try {
      const exchangeTransferCalculate = await exchangeTransfer(
        this.state.currencyExchangeDetails,
        this.state.chosenItemTransfer,
        'PL', // Default origin country
        this.state.chosenCountry.chosenCountryCode,
        this.abortController,
      )

      const transferFees = exchangeTransferCalculate.transferFees
      const isChosenItemTransferValid = transferFees.some(
        (element: TransferFeesObj): boolean =>
          element.transfer_type === this.state.chosenItemTransfer.chosenItemTransferType &&
          element.fee === this.state.chosenItemTransfer.chosenItemTransferFee,
      )

      this.setState(
        (prevState) => ({
          currencyExchangeDetails: {
            ...prevState.currencyExchangeDetails,
            [stateNameUpdate]: `${exchangeTransferCalculate.calculation[stateUpdateItem]}`,
          },
          transferFees,
          exchangeCalculation: exchangeTransferCalculate.calculation,
          chosenItemTransfer: isChosenItemTransferValid
            ? prevState.chosenItemTransfer
            : {
                chosenItemTransferType: transferFees[0]?.transfer_type || '',
                chosenItemTransferFee: transferFees[0]?.fee || '',
              },
          errorStatusMessage: Number(exchangeTransferCalculate.calculation.sold_amount) === 0.0 ? 'MIN_AMOUNT' : '',
        }),
        () => {
          this.startTimer()
        },
      )
    } catch (error) {
      let errorStatusMessage = JSON.parse(error.response)?.error?.toUpperCase().replace(/ /g, '_') || ''

      if (errorStatusMessage === 'MISSING_PARAMS') {
        if (!this.state.currencyExchangeDetails.counterAmount) {
          errorStatusMessage = 'MISSING_PARAM_COUNTER_AMOUNT'
        } else if (!this.state.currencyExchangeDetails.baseAmount) {
          errorStatusMessage = 'MISSING_PARAM_BASE_AMOUNT'
        }
      }

      this.setState({
        errorStatusMessage,
      })
    }
  }

  public determineErrorMessage = (
    errorStatusMessage: string,
    typeAmount: CurrencyExchangeDetails['counterAmount'] | CurrencyExchangeDetails['baseAmount'] | '' = '',
  ): TFunctionDetailedResult | null => {
    switch (errorStatusMessage) {
      case 'CURRENCY_NOT_SUPPORTED':
        return i18n('calculatorForegin.noCurrenciesSupportedInfo')
      case 'MISSING_PARAM_COUNTER_AMOUNT':
        return typeAmount === 'counterAmount' ? i18n('calculatorForegin.enterAmount') : null
      case 'MISSING_PARAM_BASE_AMOUNT':
        return typeAmount === 'baseAmount' ? i18n('calculatorForegin.enterAmount') : null
      case 'MIN_AMOUNT_COUNTER':
        return typeAmount === 'counterAmount' ? i18n('calculatorForegin.minAmount') : null
      case 'MIN_AMOUNT_BASE':
        return typeAmount === 'baseAmount' ? i18n('calculatorForegin.minAmount') : null
      case 'CURRENCY_COUNTRY_PAIR_NOT_SUPPORTED':
        return i18n('calculatorForegin.noCountrySupportedInfo')
          ? i18n('calculatorForegin.noCountrySupportedInfo')
          : null
      default:
        return null
    }
  }

  public getExchangeAmountErrorMessage = (
    typeAmount: CurrencyExchangeDetails['counterAmount'] | CurrencyExchangeDetails['baseAmount'] | '' = '',
    errorStatusMessage: CalculatorFormViewState['errorStatusMessage'] = this.state.errorStatusMessage || '',
  ): TFunctionDetailedResult | null => {
    return this.determineErrorMessage(errorStatusMessage, typeAmount)
  }

  public hasExchangeAmountError = (
    typeAmount: CurrencyExchangeDetails['counterAmount'] | CurrencyExchangeDetails['baseAmount'],
  ): boolean => {
    return this.determineErrorMessage(this.state.errorStatusMessage, typeAmount) !== null
  }

  public getOriginalTransferFee = (transferType: ChosenItemTransfer['chosenItemTransferType']): string | undefined => {
    const transferFee = this.state.transferFees.find((fee) => fee.transfer_type === transferType)
    return transferFee?.fee_original
  }

  public setErrorNetworkStatus = async (networkType: NetworkStatusType): Promise<void> => {
    this.setState({
      errorNetworkStatus: networkType,
    })
  }

  public hasAmountChanged = (prevState: CalculatorFormViewState, amountName: string): boolean => {
    return prevState.currencyExchangeDetails[amountName] !== this.state.currencyExchangeDetails[amountName]
  }

  public isCalculatorFormViewUpdate = (prevState: CalculatorFormViewState): boolean => {
    const { baseAmount, counterAmount } = this.state.currencyExchangeDetails
    const baseChanged = this.hasAmountChanged(prevState, 'baseAmount') && !!+baseAmount
    const counterChanged = this.hasAmountChanged(prevState, 'counterAmount') && !!+counterAmount
    const transferTypeChanged =
      prevState.chosenItemTransfer.chosenItemTransferType !== this.state.chosenItemTransfer.chosenItemTransferType
    const countryChanged = prevState.chosenCountry !== this.state.chosenCountry
    const bothFilled = !!+baseAmount && !!+counterAmount

    return baseChanged || counterChanged || (transferTypeChanged && bothFilled) || (countryChanged && bothFilled)
  }

  get ftLinkParams(): string {
    const baseUrl = typeof window !== 'undefined' ? this.props.domains.base_user : ''
    const calculatorData = {
      soldCurrency: this.state.exchangeCalculation.sold_currency,
      soldAmount: this.state.exchangeCalculation.sold_amount,
      boughtCurrency: this.state.exchangeCalculation.bought_currency,
      boughtAmount: this.state.exchangeCalculation.bought_amount,
      returnTransferType: this.state.exchangeCalculation.transfer_type,
      calculateDirection: this.state.currencyExchangeDetails.direction,
    }

    return `${baseUrl}#/?calculatorData=${encodeURIComponent(JSON.stringify(calculatorData))}`
  }

  get shouldShowError(): boolean {
    return !!this.state.errorNetworkStatus && !['fast', '422'].includes(this.state.errorNetworkStatus)
  }
  // End Helpers

  // onChange funcs
  public handleAmountChange = (value: string, amountType: 'baseAmount' | 'counterAmount'): void => {
    const direction: ExchangeDirection = amountType === 'baseAmount' ? 'forward' : 'back'
    this.setState((prevState) => ({
      currencyExchangeDetails: {
        ...prevState.currencyExchangeDetails,
        [amountType]: value,
        direction,
      },
    }))
  }

  public handleBaseAmountChange = (value: string): void => {
    this.handleAmountChange(value, 'baseAmount')
  }

  public handleCounterAmountChange = (value: string): void => {
    this.handleAmountChange(value, 'counterAmount')
  }

  public handleCurrencyChange = (option: SelectOption, direction: 'baseCurrency' | 'counterCurrency'): void => {
    const prevBaseCurrency = this.state.currencyExchangeDetails.baseCurrency
    const prevCounterCurrency = this.state.currencyExchangeDetails.counterCurrency

    const newBaseCurrency = direction === 'baseCurrency' ? option.value : prevBaseCurrency
    const newCounterCurrency = direction === 'counterCurrency' ? option.value : prevCounterCurrency

    this.setState(
      (prevState) => ({
        currencyExchangeDetails: {
          ...prevState.currencyExchangeDetails,
          baseCurrency: newBaseCurrency,
          counterCurrency: newCounterCurrency,
          [direction]: option.value,
        },
      }),
      () => {
        if (
          Number(this.state.currencyExchangeDetails.baseAmount) > 0 &&
          Number(this.state.currencyExchangeDetails.counterAmount) > 0
        ) {
          this.calculateExchangeTransfer()
        } else {
          this.setState({
            errorStatusMessage: '',
          })
        }
      },
    )
  }

  public handleBaseCurrencyChange = (option: SelectOption) => {
    this.handleCurrencyChange(option, 'baseCurrency')
  }

  public handleCounterCurrencyChange = (option: SelectOption) => {
    this.handleCurrencyChange(option, 'counterCurrency')
  }

  public handleCountryChange = (option: SelectOption): void => {
    this.setState({
      chosenCountry: {
        chosenCountryCode: option.value,
        chosenCountryName: option.searchValue,
      },
    })
  }

  public handleTransferFeesChange = (option: SelectOption): void => {
    this.setState({
      chosenItemTransfer: {
        chosenItemTransferType: option.searchValue,
        chosenItemTransferFee: option.value,
      },
    })
  }
}
